import { useMemo, Children } from 'react';

import * as styles from './UserActions.sty.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  DataTableV2CellContextProvider,
  DataTableV2ColumnContextProvider,
  useDataTableV2,
} from '../../contexts/DataTableV2Context.js';
import type { Cell, Column } from '../../hooks/useTable/types.js';

/**
 * @internal
 * Floating menu for right click events.
 */
export function CellActionFloatingMenu() {
  const table = useDataTableV2();
  const { type, rowId, columnId, x, y } = table.getState()
    ?.floatingMenuInfo ?? {
    type: null,
    rowId: null,
    columnId: null,
  };

  const content = useMemo(() => {
    if (type === 'headerAction' && columnId !== null) {
      return table.getUserActionsForColumn(columnId);
    }

    if (type === 'cellAction' && columnId !== null && rowId !== null) {
      return table.getUserActionsForCell(columnId, rowId);
    }

    return { builtinActions: null, customActions: null };
  }, [rowId, columnId, type, table]);

  const menuOpen =
    type !== null &&
    columnId !== null &&
    (content.builtinActions !== null || content.customActions !== null);

  // Find the right column and cell instance to provide
  const { column, cell } = useMemo<{
    column?: Column<unknown, unknown>;
    cell?: Cell<unknown, unknown>;
  }>(() => {
    if (menuOpen === false) {
      return { column: undefined, cell: undefined };
    }

    const column = table.getColumn(columnId);

    let cell: Cell<unknown, unknown> | undefined = undefined;
    if (rowId) {
      const rowCells = table.getRow(rowId).getAllCells();
      const columnCell = rowCells.find((cell) => cell.column.id === columnId);

      cell = columnCell;
    }

    return { column, cell };
  }, [columnId, menuOpen, rowId, table]);

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (menuOpen === false) {
    return;
  }
  return (
    <Menu
      open={menuOpen}
      onOpenChange={(isOpen) => {
        if (isOpen === false) {
          table.setState((state) => {
            const newState = { ...state };
            newState.floatingMenuInfo = {
              type: null,
              rowId: null,
              columnId: null,
              x: 0,
              y: 0,
            };
            return newState;
          });
        }
      }}
      modal={true}
    >
      <Menu.Trigger>
        <button
          disabled
          type="button"
          className={styles.floatingMenuButton}
          style={{
            top: `${y}px`,
            left: `${x}px`,
          }}
          data-trigger="context-action"
        />
      </Menu.Trigger>
      {menuOpen === true && (
        <DataTableV2ColumnContextProvider value={column}>
          <DataTableV2CellContextProvider value={cell}>
            <Menu.Content>
              {content.builtinActions !== null &&
              content.builtinActions?.length !== 0 ? (
                /*
                 * Run all actions through a React.Children map so they will receive
                 * an automatic key and do not violate react key map rules.
                 */
                <Menu.Group>
                  {Children.map(content.builtinActions, (child) => child)}
                </Menu.Group>
              ) : null}
              {/*
               * Run all actions through a React.Children map so they will receive
               * an automatic key and do not violate react key map rules.
               */}
              {Children.map(content.customActions, (child) => child)}
            </Menu.Content>
          </DataTableV2CellContextProvider>
        </DataTableV2ColumnContextProvider>
      )}
    </Menu>
  );
}
